import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import DocumentTemplateApi, { loadModalTemplatePageInput } from '../api/DocumentTemplate.api';
import type { LoadableTemplate, TemplatePage } from './useDocumentTemplateLoadPage';

const pageNodes = (page: TemplatePage | undefined) =>
  (page?.documentTemplatePage.edges ?? []).flatMap((edge) => (edge ? [edge.node] : []));

/**
 * The load modal's Export All read: every custom template the org has, page by page from the network, so the
 * export never stops at one page. A failed page rejects the whole read.
 */
export function useReadAllDocumentTemplates() {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  const readAllDocumentTemplates = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const templates: LoadableTemplate[] = [];
      let after: string | undefined;
      for (;;) {
        const { data: page } = await client.query<TemplatePage>({
          query: DocumentTemplateApi.GetDocumentTemplatesState,
          variables: { input: loadModalTemplatePageInput('', after) },
          fetchPolicy: 'network-only',
        });
        templates.push(...pageNodes(page));
        const info = page.documentTemplatePage.pageInfo;
        if (!info?.hasNextPage || !info.endCursor) return templates;
        after = info.endCursor;
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
      throw caught;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return { readAllDocumentTemplates, loading, error };
}
